import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import withPosition from '../../../../hocs/withPosition/withPosition';
import { BubblePosition, Variant } from '../../types';
import { Space, StyledBubble, Wrapper } from './Bubble.styled';

interface BubbleProps {
  dataCy?: string;
  text: string;
  show: boolean;
  position?: string;
  containHtml?: boolean;
  disableHtmlRendering?: boolean;
  breakAll?: boolean;
  update?: () => void;
  variant?: Variant;
  width?: string;
}

export type Positions = Record<string, BubblePosition>;

export const positions: Positions = {
  top: BubblePosition.TOP,
  bottom: BubblePosition.BOTTOM,
  left: BubblePosition.LEFT,
  right: BubblePosition.RIGHT,
};

const Bubble = ({
  dataCy,
  text,
  show,
  position,
  containHtml,
  disableHtmlRendering = false,
  breakAll = false,
  update,
  variant = Variant.DARK,
  width,
}: BubbleProps) => {
  const [internalPosition, setInternalPosition] = useState<BubblePosition>(
    BubblePosition.TOP,
  );
  const [height, setHeight] = useState(0);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (show && !!update) {
      update();
    }
  }, [show, update]);

  useEffect(() => {
    if (position && positions[position]) {
      setInternalPosition(positions[position] as BubblePosition);
    }
  }, [position]);

  useEffect(() => {
    if (ref.current) {
      setHeight(ref.current.offsetHeight);
    }
  }, [ref]);

  useEffect(() => {
    update?.();
  }, [text, update]);

  const dangerouslySetInnerHTML = useMemo(() => ({ __html: text }), [text]);

  const renderText = useCallback(() => {
    if (disableHtmlRendering) {
      return (
        <StyledBubble
          ref={ref}
          data-cy={dataCy}
          $show={show}
          $position={internalPosition}
          $containHtml={containHtml}
          $breakAll={breakAll}
          $variant={variant}
          $width={width}
        >
          {text}
        </StyledBubble>
      );
    }

    return (
      <StyledBubble
        ref={ref}
        data-cy={dataCy}
        dangerouslySetInnerHTML={dangerouslySetInnerHTML}
        $show={show}
        $position={internalPosition}
        $containHtml={containHtml}
        $breakAll={breakAll}
        $variant={variant}
        $width={width}
      />
    );
  }, [
    breakAll,
    containHtml,
    dangerouslySetInnerHTML,
    dataCy,
    disableHtmlRendering,
    internalPosition,
    show,
    text,
    variant,
    width,
  ]);

  return (
    <Wrapper $position={internalPosition}>
      {renderText()}
      <Space $show={show} $height={`${height}px`} $position={internalPosition} />
    </Wrapper>
  );
};

const BubbleWithPosition = withPosition(Bubble);

export default Object.assign(BubbleWithPosition, {
  BubblePosition,
});
